import { useEffect, useState } from "react";
import { ActivityIndicator, ScrollView, View } from "react-native";
import {
  Activity,
  Beaker,
  CalendarDays,
  Clock3,
  Ellipsis,
  FileText,
  FlaskConical,
  FlaskRound,
  HeartHandshake,
  Merge,
  Microscope,
  MoveRight,
  Pipette,
  Snowflake,
  Sprout,
  Syringe,
  ThermometerSun,
  Trash2,
  type LucideIcon,
} from "lucide-react-native";
import {
  ActionBanner,
  Dialog,
  DialogBodyText,
  DialogHeader,
  FieldMessage,
  InlineOptionPicker,
  SupportingSubtitleText,
  PrimaryButton,
  SecondaryButton,
  DateField,
  MoonFormField,
  MoonTimeField,
  MoonValueInput,
  SectionTitle,
  TextField,
  LoadingState,
} from "@louise/moon-ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@louise/moon-ui/primitives/Accordion";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { EMBER } from "../../onboarding/theme";
import {
  createIdempotencyKey,
  useRecordSpecimenMaterialOperation,
  useSpecimens,
  type SpecimenMaterialOperation,
  type SpecimenMaterialOperationAction,
} from "@src/packages/care/cryovault/useSpecimens";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import { useLocale } from "@src/packages/i18n/useLocale";
import { formatDate } from "@src/packages/i18n/formatters";
import {
  useCycles,
  useCycleTypeLabels,
  type Cycle,
} from "@src/packages/cycles/hooks/useCycles";
import { getFrozenSpecimenTreatmentTargets } from "@src/packages/care/cryovault/frozenSpecimenTreatmentTarget";
import type {
  CryoSpecimenDetail,
  CryoSpecimenType,
} from "./CryoSpecimenDetailModal";
import { semanticColors } from "@louise/theme/tokens";
// TODO: replace this extension when the pinned client contract includes the
// dashboard thawingInfo.survivedQuantity field.
type TypedThawingInfo = NonNullable<
  SpecimenMaterialOperation["thawingInfo"]
> & {
  survivedQuantity?: number | null;
};
type TypedSpecimenMaterialOperation = Omit<
  SpecimenMaterialOperation,
  "thawingInfo"
> & {
  thawingInfo?: TypedThawingInfo | null;
};

/**
 * One answer the event picker offers: the lab action it records, and the
 * words it is offered in.
 *
 * Several answers can record the same action. "Used for IUI", "Used for IVF or
 * ICSI" and "Something else" are all a `use`; what tells them apart travels as
 * the operation's `reason`.
 */
export type CryoSpecimenLifecycleChoice = {
  id: string;
  action: SpecimenMaterialOperationAction;
  /** The answer's label, which also titles the dialog that records it. */
  labelKey: string;
  icon: LucideIcon;
  /**
   * How it was used, when the action alone does not say.
   *
   * Stable phrases rather than translated copy: this is provenance on a
   * clinical record, and it must not read differently per locale.
   */
  reason?: string;
};

const TRANSFERRED: CryoSpecimenLifecycleChoice = {
  id: "transferred",
  action: "transfer",
  labelKey: "cryo_vault.modal.lifecycle_event_transferred",
  icon: MoveRight,
};
const DONATED: CryoSpecimenLifecycleChoice = {
  id: "donated",
  action: "donate",
  labelKey: "cryo_vault.modal.lifecycle_event_donated",
  icon: HeartHandshake,
};
const DISCARDED: CryoSpecimenLifecycleChoice = {
  id: "discarded",
  action: "discard",
  labelKey: "cryo_vault.modal.lifecycle_event_discarded",
  icon: Trash2,
};
const SOMETHING_ELSE: CryoSpecimenLifecycleChoice = {
  id: "something_else",
  action: "use",
  labelKey: "cryo_vault.modal.lifecycle_event_something_else",
  icon: Ellipsis,
  reason: "something_else",
};
const THAWED_AND_FERTILIZED: CryoSpecimenLifecycleChoice = {
  id: "thawed_and_fertilized",
  action: "fertilize",
  labelKey: "cryo_vault.modal.lifecycle_event_thawed_and_fertilized",
  icon: Merge,
};
const USED_FOR_IUI: CryoSpecimenLifecycleChoice = {
  id: "used_for_iui",
  action: "use",
  labelKey: "cryo_vault.modal.lifecycle_event_used_for_iui",
  icon: Syringe,
  reason: "used_for_iui",
};
const USED_FOR_IVF_OR_ICSI: CryoSpecimenLifecycleChoice = {
  id: "used_for_ivf_or_icsi",
  action: "use",
  labelKey: "cryo_vault.modal.lifecycle_event_used_for_ivf_or_icsi",
  icon: FlaskConical,
  reason: "used_for_ivf_or_icsi",
};
const THAW: CryoSpecimenLifecycleChoice = {
  id: "thaw",
  action: "thaw",
  labelKey: "cryo_vault.modal.lifecycle_event_thaw",
  icon: ThermometerSun,
};
const BIOPSY: CryoSpecimenLifecycleChoice = {
  id: "biopsy",
  action: "biopsy",
  labelKey: "cryo_vault.modal.lifecycle_event_biopsy",
  icon: Pipette,
};
const TEST: CryoSpecimenLifecycleChoice = {
  id: "test",
  action: "test",
  labelKey: "cryo_vault.modal.lifecycle_event_test",
  icon: Microscope,
};
const CULTURE: CryoSpecimenLifecycleChoice = {
  id: "culture",
  action: "culture",
  labelKey: "cryo_vault.modal.lifecycle_event_culture",
  icon: FlaskRound,
};
const MATURE: CryoSpecimenLifecycleChoice = {
  id: "mature",
  action: "mature",
  labelKey: "cryo_vault.modal.lifecycle_event_mature",
  icon: Sprout,
};
const REFREEZE: CryoSpecimenLifecycleChoice = {
  id: "refreeze",
  action: "freeze",
  labelKey: "cryo_vault.modal.lifecycle_event_refreeze",
  icon: Snowflake,
};

/**
 * What a patient can record, per kind of specimen.
 *
 * What happened to it comes first, because that is what she is here to say;
 * the lab's own steps fold away beneath it. Reserve and release are not
 * offered: picking material for a cycle keeps the pick in the cycle's
 * snapshot rather than putting it on hold, and older holds still show in the
 * specimen's history.
 */
const LIFECYCLE_CHOICES: Record<
  CryoSpecimenType,
  {
    outcomes: readonly CryoSpecimenLifecycleChoice[];
    labSteps: readonly CryoSpecimenLifecycleChoice[];
  }
> = {
  embryo: {
    outcomes: [TRANSFERRED, DONATED, DISCARDED, SOMETHING_ELSE],
    labSteps: [THAW, BIOPSY, TEST, CULTURE, REFREEZE],
  },
  oocyte: {
    outcomes: [THAWED_AND_FERTILIZED, DONATED, DISCARDED, SOMETHING_ELSE],
    labSteps: [THAW, MATURE, REFREEZE],
  },
  sperm: {
    outcomes: [USED_FOR_IUI, USED_FOR_IVF_OR_ICSI, DONATED, DISCARDED],
    labSteps: [THAW, TEST, REFREEZE],
  },
};

export function getLifecycleChoicesForSpecimen(specimen: CryoSpecimenDetail): {
  outcomes: readonly CryoSpecimenLifecycleChoice[];
  labSteps: readonly CryoSpecimenLifecycleChoice[];
} {
  return LIFECYCLE_CHOICES[specimen.type];
}

function getMaterialUnit(specimenType: CryoSpecimenType): "count" | "vial" {
  return specimenType === "sperm" ? "vial" : "count";
}

function getMaterialUnitLabel(
  t: TranslationFunction,
  unit: "count" | "vial",
): string {
  return unit === "vial"
    ? t("cryo_vault.modal.lifecycle_event_unit_vial")
    : t("cryo_vault.modal.lifecycle_event_unit_count");
}

function getLocalEventDate(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function getLocalEventTime(): string {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
}

function buildOccurredAt(date: string, time: string): string {
  const offsetMinutes = -new Date(`${date}T${time}:00`).getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absoluteMinutes = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absoluteMinutes / 60)).padStart(2, "0");
  const minutes = String(absoluteMinutes % 60).padStart(2, "0");
  return `${date}T${time}:00${sign}${hours}:${minutes}`;
}

function isValidQuantity(value: string, unit: "count" | "vial"): boolean {
  if (!/^(?:0|[1-9]\d*)(?:\.\d+)?$/.test(value.trim())) return false;
  if (Number(value) <= 0) return false;
  return unit === "count" || unit === "vial" ? !value.includes(".") : true;
}

function isValidPercentage(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;
  if (!/^(?:0|[1-9]\d*)(?:\.\d+)?$/.test(trimmed)) return false;
  const numericValue = Number(trimmed);
  return numericValue >= 0 && numericValue <= 100;
}

function isValidSurvivingQuantity(
  value: string,
  thawedQuantity: string,
): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;
  if (!/^\d+$/.test(trimmed)) return false;
  return Number(trimmed) <= Number(thawedQuantity);
}

function parsePercentage(value: string): number | null {
  const trimmed = value.trim();
  return trimmed ? Number(trimmed) : null;
}

function getSurvivalRate(
  thawedQuantity: string,
  survivedQuantity: string,
): number | null {
  if (!isValidSurvivingQuantity(survivedQuantity, thawedQuantity)) return null;
  if (!survivedQuantity.trim()) return null;
  return (Number(survivedQuantity) / Number(thawedQuantity)) * 100;
}

function formatPercentage(value: number): string {
  return `${Number.isInteger(value) ? value : Number(value.toFixed(2))}%`;
}

const CYCLE_LINKED_ACTIONS: ReadonlySet<SpecimenMaterialOperationAction> =
  new Set(["thaw", "fertilize", "mature", "use", "transfer"]);

/** The cycle picker's "no cycle" answer; cycle ids are never this. */
const NO_LINKED_CYCLE = "no-linked-cycle";

/**
 * Cycles this specimen could have been used in: the treatments that take its
 * material type, plus any cycle it is already linked to. An egg-freezing
 * cycle is never offered for a sperm sample.
 */
function getRelevantCycles(
  specimen: CryoSpecimenDetail,
  cycles: readonly Cycle[],
): Cycle[] {
  const targets = getFrozenSpecimenTreatmentTargets(
    specimen.type === "sperm" ? "semen" : specimen.type,
  );
  const linkedCycleIds = new Set(
    [specimen.useCycleId, specimen.transferCycleId].filter(Boolean),
  );
  return cycles.filter(
    (cycle) =>
      targets.includes(cycle.cycleType) || linkedCycleIds.has(cycle.id),
  );
}

function newestFirst(cycles: readonly Cycle[]): Cycle[] {
  return [...cycles].sort((a, b) =>
    (b.startDate ?? b.createdAt ?? "").localeCompare(
      a.startDate ?? a.createdAt ?? "",
    ),
  );
}

/**
 * The cycle the event most likely belongs to: the one already linked, else
 * the latest cycle she picked this specimen for when it started. She can
 * change it or clear it before saving.
 */
function getSuggestedCycleId(
  specimen: CryoSpecimenDetail,
  action: SpecimenMaterialOperationAction,
  cycles: readonly Cycle[],
): string | null {
  const linkedCycleId =
    action === "transfer" ? specimen.transferCycleId : specimen.useCycleId;
  if (linkedCycleId) return linkedCycleId;
  const pickedFor = newestFirst(cycles).find((cycle) =>
    (cycle.contextSnapshot?.materialIds ?? []).includes(specimen.id),
  );
  return pickedFor?.id ?? null;
}

/** Two-column tiles for one group of answers. Choosing one moves on. */
function LifecycleChoiceTiles({
  choices,
  onSelect,
}: {
  choices: readonly CryoSpecimenLifecycleChoice[];
  onSelect: (choice: CryoSpecimenLifecycleChoice) => void;
}) {
  const t = useTranslations();
  return (
    <InlineOptionPicker
      options={choices.map((choice) => ({
        id: choice.id,
        label: t(choice.labelKey),
        icon: choice.icon,
        action: "specimen_lifecycle_choice",
        testID: `specimen-lifecycle-choice-${choice.id}`,
      }))}
      // Choosing an event moves on, so no event stays selected here.
      value=""
      onChange={(next) => {
        const choice = choices.find((candidate) => candidate.id === next);
        if (choice) onSelect(choice);
      }}
      wrap
      columns={2}
    />
  );
}

export function CryoSpecimenLifecycleActionPicker({
  visible,
  specimen,
  onClose,
  onSelect,
}: {
  visible: boolean;
  specimen: CryoSpecimenDetail | null;
  onClose: () => void;
  onSelect: (choice: CryoSpecimenLifecycleChoice) => void;
}) {
  const t = useTranslations();
  if (!visible || !specimen) return null;
  const { outcomes, labSteps } = getLifecycleChoicesForSpecimen(specimen);

  return (
    <Dialog
      visible
      onClose={onClose}
      closeButton="minimal"
      testID="cryo-specimen-lifecycle-picker"
    >
      {/* The shared header; the help reads as its second line of body copy
          rather than a fainter style of its own. */}
      <DialogHeader
        title={t("cryo_vault.modal.record_lifecycle_event")}
        subtitle={specimen.label}
        style={{ width: "100%" }}
      />
      <DialogBodyText>
        {t("cryo_vault.modal.lifecycle_event_help")}
      </DialogBodyText>
      <View style={{ marginTop: 18, gap: 10 }}>
        <SectionTitle>{t("cryo_vault.modal.what_happened_to_it")}</SectionTitle>
        <LifecycleChoiceTiles choices={outcomes} onSelect={onSelect} />
      </View>
      {/* The lab's own steps, for someone keeping the full record. Folded
          so they never stand level with what happened to it. */}
      <Accordion type="single" collapsible style={{ marginTop: 18 }}>
        <AccordionItem value="lab-steps">
          <AccordionTrigger
            action={{
              expand: "expand_specimen_lab_steps",
              collapse: "collapse_specimen_lab_steps",
            }}
            accessibilityLabel={t("cryo_vault.modal.lab_steps")}
            testID="specimen-lifecycle-lab-steps"
            className="items-center py-0"
          >
            <SectionTitle>{t("cryo_vault.modal.lab_steps")}</SectionTitle>
          </AccordionTrigger>
          <AccordionContent className="pb-0">
            <View style={{ paddingTop: 10 }}>
              <LifecycleChoiceTiles choices={labSteps} onSelect={onSelect} />
            </View>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <View style={{ marginTop: 20, marginBottom: 2 }}>
        <SecondaryButton
          label={t("appointment_detail.cancel")}
          onPress={onClose}
        />
      </View>
    </Dialog>
  );
}

export function RecordCryoSpecimenLifecycleDialog({
  visible,
  specimen,
  choice,
  onClose,
}: {
  visible: boolean;
  specimen: CryoSpecimenDetail | null;
  /** The answer she picked, which says what to record and how. */
  choice: CryoSpecimenLifecycleChoice | null;
  onClose: () => void;
}) {
  const t = useTranslations();
  const { locale } = useLocale();
  const cycleTypeLabels = useCycleTypeLabels();
  const { data: cycles } = useCycles();
  const action = choice?.action ?? null;
  const choiceId = choice?.id ?? null;
  const recordOperation = useRecordSpecimenMaterialOperation();
  const semenSpecimensQuery = useSpecimens("semen", {
    enabled: visible && action === "fertilize",
  });
  const [eventDate, setEventDate] = useState(getLocalEventDate);
  const [eventTime, setEventTime] = useState(getLocalEventTime);
  const [quantity, setQuantity] = useState("1");
  const [notes, setNotes] = useState("");
  const [thawMotility, setThawMotility] = useState("");
  const [thawViability, setThawViability] = useState("");
  const [thawSurvivedQuantity, setThawSurvivedQuantity] = useState("");
  const [semenSpecimenId, setSemenSpecimenId] = useState<string | null>(null);
  const [semenQuantity, setSemenQuantity] = useState("1");
  const [outputLabel, setOutputLabel] = useState("");
  // `undefined` until she picks, so the suggestion can follow cycles that
  // load after the dialog opens.
  const [chosenCycleId, setChosenCycleId] = useState<string | null | undefined>(
    undefined,
  );
  const [eventIdempotencyKey, setEventIdempotencyKey] =
    useState(createIdempotencyKey);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setEventDate(getLocalEventDate());
    setEventTime(getLocalEventTime());
    setQuantity("1");
    setNotes("");
    setThawMotility("");
    setThawViability("");
    setThawSurvivedQuantity("");
    setSemenSpecimenId(null);
    setSemenQuantity("1");
    setOutputLabel("");
    setChosenCycleId(undefined);
    setEventIdempotencyKey(createIdempotencyKey());
    setSaveError(null);
  }, [choiceId, visible]);

  if (!visible || !specimen || !choice || !action) return null;

  const unit = getMaterialUnit(specimen.type);
  const isSperm = specimen.type === "sperm";
  const isFertilization = action === "fertilize";
  const isThaw = action === "thaw";
  const isSaving = recordOperation.isPending;
  const isCycleLinked = CYCLE_LINKED_ACTIONS.has(action);
  const cycleOptions = newestFirst(getRelevantCycles(specimen, cycles ?? []));
  const relatedCycleId = !isCycleLinked
    ? null
    : chosenCycleId === undefined
      ? getSuggestedCycleId(specimen, action, cycleOptions)
      : chosenCycleId;
  const getCycleOptionLabel = (cycle: Cycle) => {
    const typeLabel = cycleTypeLabels[cycle.cycleType] ?? cycle.cycleType;
    const startDate = cycle.startDate
      ? formatDate(cycle.startDate, locale, {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : null;
    return startDate ? `${typeLabel} · ${startDate}` : typeLabel;
  };
  const semenSpecimens = (semenSpecimensQuery.data ?? []).filter(
    ({ status }) => !["used", "discarded", "donated"].includes(status),
  );
  const selectedSemenSpecimen = semenSpecimens.find(
    ({ id }) => id === semenSpecimenId,
  );
  const percentagesValid =
    isValidPercentage(thawViability) &&
    (!isSperm || isValidPercentage(thawMotility));
  const survivingQuantityValid = isValidSurvivingQuantity(
    thawSurvivedQuantity,
    quantity,
  );
  const thawMeasurementsValid = isSperm
    ? percentagesValid
    : survivingQuantityValid;
  const thawSurvivalRate =
    !isSperm && isThaw ? getSurvivalRate(quantity, thawSurvivedQuantity) : null;
  const fertilizationValid =
    !isFertilization ||
    (Boolean(selectedSemenSpecimen) &&
      isValidQuantity(semenQuantity, "vial") &&
      outputLabel.trim().length > 0);
  const formValid =
    isValidQuantity(quantity, unit) &&
    /^\d{4}-\d{2}-\d{2}$/.test(eventDate.trim()) &&
    /^\d{2}:\d{2}$/.test(eventTime.trim()) &&
    (!isThaw || thawMeasurementsValid) &&
    fertilizationValid;

  const handleSave = async () => {
    if (!formValid) return;
    if (isFertilization && !selectedSemenSpecimen) return;
    setSaveError(null);
    const fertilizationInput =
      isFertilization && selectedSemenSpecimen
        ? {
            specimenId: selectedSemenSpecimen.id,
            quantity: semenQuantity.trim(),
            unit: "vial" as const,
            disposition: "consumed" as const,
          }
        : null;

    const operation: TypedSpecimenMaterialOperation = {
      clientRequestId: eventIdempotencyKey,
      action,
      occurredAt: buildOccurredAt(eventDate.trim(), eventTime.trim()),
      quantity: quantity.trim(),
      unit,
      hasDecisionAndConsent: true,
      ...(relatedCycleId ? { cycleId: relatedCycleId } : {}),
      ...(choice.reason ? { reason: choice.reason } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      ...(isThaw
        ? {
            thawingInfo: isSperm
              ? {
                  motilityPostThaw: parsePercentage(thawMotility),
                  viability: parsePercentage(thawViability),
                }
              : {
                  ...(thawSurvivedQuantity.trim()
                    ? {
                        survivedQuantity: Number(thawSurvivedQuantity.trim()),
                      }
                    : {}),
                },
          }
        : {}),
      ...(fertilizationInput
        ? {
            additionalInputs: [fertilizationInput],
            outputs: [
              {
                newAliquot: { label: outputLabel.trim() },
                quantity: quantity.trim(),
                unit: "count" as const,
                disposition: "produced" as const,
              },
            ],
          }
        : {}),
    };

    try {
      await recordOperation.mutateAsync({ id: specimen.id, operation });
      onClose();
    } catch (error) {
      setSaveError(
        error instanceof Error
          ? error.message
          : t("cryo_vault.modal.lifecycle_event_error"),
      );
    }
  };

  return (
    <Dialog
      visible
      onClose={isSaving ? () => undefined : onClose}
      closeButton="minimal"
      surfaceStyle={{ maxHeight: 680 }}
      contentStyle={{ maxHeight: 680 }}
      testID="cryo-specimen-lifecycle-dialog"
    >
      <DialogHeader
        title={t(choice.labelKey)}
        subtitle={specimen.label}
        style={{ width: "100%", marginBottom: 16 }}
      />
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ gap: 14, paddingBottom: 4 }}
      >
        <MoonFormField
          label={t("cryo_vault.modal.lifecycle_event_quantity")}
          labelIcon={Activity}
        >
          <MoonValueInput
            accessibilityLabel={t("cryo_vault.modal.lifecycle_event_quantity")}
            value={quantity}
            onChangeText={setQuantity}
            placeholder={t(
              "cryo_vault.modal.lifecycle_event_quantity_placeholder",
            )}
            keyboardType="decimal-pad"
            suffix={getMaterialUnitLabel(t, unit)}
          />
        </MoonFormField>

        {isFertilization && (
          <View style={{ gap: 12 }}>
            <MoonFormField
              label={t("cryo_vault.modal.fertilization_source_label")}
              labelIcon={Activity}
            >
              <View style={{ gap: 8 }}>
                {semenSpecimensQuery.isLoading ? (
                  <LoadingState style={{ paddingVertical: 8 }} />
                ) : semenSpecimens.length > 0 ? (
                  <InlineOptionPicker
                    options={semenSpecimens.map((semenSpecimen, index) => ({
                      id: semenSpecimen.id,
                      label:
                        semenSpecimen.sampleId?.trim() ||
                        t("cryo_vault.labels.sample_label").replace(
                          "${n}",
                          String(index + 1),
                        ),
                      action: "specimen_lifecycle_choice",
                    }))}
                    value={semenSpecimenId ?? ""}
                    onChange={setSemenSpecimenId}
                    wrap
                    columns={2}
                  />
                ) : (
                  <SupportingSubtitleText style={{ marginTop: 0 }}>
                    {t("cryo_vault.modal.fertilization_source_empty")}
                  </SupportingSubtitleText>
                )}
              </View>
            </MoonFormField>
            <TextField
              label={t("cryo_vault.modal.fertilization_source_quantity")}
              labelIcon={Activity}
              accessibilityLabel={t(
                "cryo_vault.modal.fertilization_source_quantity",
              )}
              value={semenQuantity}
              onChangeText={setSemenQuantity}
              keyboardType="decimal-pad"
            />
            <TextField
              label={t("cryo_vault.modal.fertilization_output_label")}
              labelIcon={FileText}
              accessibilityLabel={t(
                "cryo_vault.modal.fertilization_output_label",
              )}
              value={outputLabel}
              onChangeText={setOutputLabel}
              placeholder={t(
                "cryo_vault.modal.fertilization_output_placeholder",
              )}
            />
          </View>
        )}

        <View style={{ flexDirection: "row", gap: 10 }}>
          <View style={{ flex: 1 }}>
            <DateField
              label={t("cryo_vault.modal.lifecycle_event_date")}
              labelIcon={CalendarDays}
              value={eventDate}
              onChange={setEventDate}
            />
          </View>
          <View style={{ flex: 1 }}>
            <MoonTimeField
              label={t("cryo_vault.modal.lifecycle_event_time")}
              labelIcon={Clock3}
              value={eventTime}
              onChange={setEventTime}
              placeholder={t(
                "cryo_vault.modal.lifecycle_event_time_placeholder",
              )}
            />
          </View>
        </View>

        {isCycleLinked && cycleOptions.length > 0 && (
          <MoonFormField
            label={t("cryo_vault.modal.lifecycle_event_linked_cycle")}
            labelIcon={Beaker}
          >
            <InlineOptionPicker
              options={[
                ...cycleOptions.map((cycle) => ({
                  id: cycle.id,
                  label: getCycleOptionLabel(cycle),
                  action: "specimen_lifecycle_cycle",
                })),
                {
                  id: NO_LINKED_CYCLE,
                  label: t("cryo_vault.modal.lifecycle_event_no_linked_cycle"),
                  action: "specimen_lifecycle_no_cycle",
                },
              ]}
              value={relatedCycleId ?? NO_LINKED_CYCLE}
              onChange={(next) =>
                setChosenCycleId(next === NO_LINKED_CYCLE ? null : next)
              }
              wrap
              columns={2}
            />
          </MoonFormField>
        )}

        {isThaw && (
          <View style={{ gap: 12 }}>
            {isSperm ? (
              <View style={{ flexDirection: "row", gap: 10 }}>
                <View style={{ flex: 1 }}>
                  <MoonFormField
                    label={t("cryo_vault.modal.lifecycle_thaw_motility")}
                    labelIcon={Activity}
                  >
                    <MoonValueInput
                      accessibilityLabel={t(
                        "cryo_vault.modal.lifecycle_thaw_motility",
                      )}
                      value={thawMotility}
                      onChangeText={setThawMotility}
                      keyboardType="decimal-pad"
                      suffix="%"
                    />
                  </MoonFormField>
                </View>
                <View style={{ flex: 1 }}>
                  <MoonFormField
                    label={t("cryo_vault.modal.lifecycle_thaw_viability")}
                    labelIcon={Activity}
                  >
                    <MoonValueInput
                      accessibilityLabel={t(
                        "cryo_vault.modal.lifecycle_thaw_viability",
                      )}
                      value={thawViability}
                      onChangeText={setThawViability}
                      keyboardType="decimal-pad"
                      suffix="%"
                    />
                  </MoonFormField>
                </View>
              </View>
            ) : (
              <>
                <MoonFormField
                  label={t(
                    "cryo_vault.modal.lifecycle_thaw_surviving_quantity",
                  )}
                  labelIcon={Activity}
                >
                  <MoonValueInput
                    accessibilityLabel={t(
                      "cryo_vault.modal.lifecycle_thaw_surviving_quantity",
                    )}
                    value={thawSurvivedQuantity}
                    onChangeText={setThawSurvivedQuantity}
                    placeholder={t(
                      "cryo_vault.modal.lifecycle_thaw_surviving_quantity_placeholder",
                    )}
                    keyboardType="number-pad"
                    suffix={getMaterialUnitLabel(t, unit)}
                  />
                </MoonFormField>
                <MoonFormField
                  label={t("cryo_vault.modal.lifecycle_thaw_survival_rate")}
                  labelIcon={Activity}
                >
                  <View
                    accessibilityLabel={t(
                      "cryo_vault.modal.lifecycle_thaw_survival_rate",
                    )}
                    style={{
                      borderWidth: 1,
                      borderColor: semanticColors.border.strong,
                      borderRadius: 12,
                      backgroundColor: semanticColors.surface.muted,
                      paddingHorizontal: 14,
                      paddingVertical: 11,
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{ color: EMBER.colors.text, fontSize: 14 }}
                    >
                      {thawSurvivalRate === null
                        ? t(
                            "cryo_vault.modal.lifecycle_thaw_survival_rate_unrecorded",
                          )
                        : formatPercentage(thawSurvivalRate)}
                    </Text>
                  </View>
                </MoonFormField>
              </>
            )}
            {isThaw && !thawMeasurementsValid && (
              <FieldMessage>
                {isSperm
                  ? t("cryo_vault.modal.lifecycle_thaw_percentage_error")
                  : t(
                      "cryo_vault.modal.lifecycle_thaw_surviving_quantity_error",
                    )}
              </FieldMessage>
            )}
          </View>
        )}

        <TextField
          label={t("cryo_vault.modal.lifecycle_event_note")}
          labelIcon={FileText}
          accessibilityLabel={t("cryo_vault.modal.lifecycle_event_note")}
          value={notes}
          onChangeText={setNotes}
          placeholder={t("cryo_vault.modal.lifecycle_event_note_placeholder")}
          notes
        />

        {saveError ? (
          <ActionBanner variant="important" message={saveError} />
        ) : !formValid ? (
          <FieldMessage>
            {isFertilization
              ? t("cryo_vault.modal.fertilization_required")
              : t("cryo_vault.modal.lifecycle_event_required")}
          </FieldMessage>
        ) : null}
      </ScrollView>
      <View
        style={{
          flexDirection: "row",
          gap: 8,
          paddingTop: 14,
          marginTop: 16,
        }}
      >
        <SecondaryButton
          flex={1}
          label={t("appointment_detail.cancel")}
          onPress={onClose}
          disabled={isSaving}
        />
        <PrimaryButton
          flex={1.35}
          label={
            isSaving
              ? t("cryo_vault.modal.saving_changes")
              : t("cryo_vault.modal.record_event")
          }
          onPress={handleSave}
          disabled={isSaving || !formValid}
          icon={
            isSaving ? (
              <ActivityIndicator color={EMBER.colors.textLight} size="small" />
            ) : undefined
          }
        />
      </View>
    </Dialog>
  );
}
